import { openDB, type IDBPDatabase } from 'idb';
import { LocalStorageKeys } from '@/app/lib/constants';
import {
  comparePlayVersions,
  type CompletedBoard,
  type SyncedPlay,
  type SyncMeta,
  type SyncStatus,
  type SyncStore,
} from '@puzzlio/core';

/*
  The browser's copy of the player's plays: every play of every board, kept in step with the server by
  /api/sync (see sync.ts). Pages read from here rather than from the network.

  Held in memory, one map per scope (the player id the plays belong to), and written through to IndexedDB in
  the background. A scope is read from IndexedDB once, when a page first needs it; from then on reads are
  synchronous, as the play store's seed requires, and the sync engine runs against the memory copy.

  IndexedDB is a cache the browser may clear (Safari after a week away, private windows on close, eviction
  under disk pressure), and it can be missing altogether (some embedded iframes). Nothing here depends on
  it: without it the memory copy works alone, and the next sync fills it from the server. Only plays not yet
  sent are at risk, which is why the sync runs at every point a page could be closed.
*/

export interface StoredPlay extends SyncedPlay {
  dirty: boolean;
}

interface PlayRecord extends StoredPlay {
  scope: string;
  changedAt: number;
}

interface ScopeCache {
  plays: Map<string, PlayRecord>;
  meta: SyncMeta | null;
}

type Op =
  | { kind: 'put'; record: PlayRecord }
  | { kind: 'delete'; scope: string; cid: string }
  | { kind: 'meta'; scope: string; meta: SyncMeta | null };

const DB_NAME = 'inkwell-plays';

let dbPromise: Promise<IDBPDatabase | null> | null = null;

function database(): Promise<IDBPDatabase | null> {
  dbPromise ??=
    typeof indexedDB === 'undefined'
      ? Promise.resolve(null)
      : openDB(DB_NAME, 1, {
          upgrade(db) {
            db.createObjectStore('plays', { keyPath: ['scope', 'cid'] }).createIndex('scope', 'scope');
            db.createObjectStore('meta', { keyPath: 'scope' });
          },
        }).catch((cause) => {
          console.warn('[plays] IndexedDB unavailable; keeping plays in memory only', cause);
          return null;
        });
  return dbPromise;
}

/** Writes in order, each batch one IndexedDB transaction. A failure loses that batch's persistence only. */
let writes: Promise<void> = Promise.resolve();

function persist(ops: Op[]) {
  if (!ops.length) return;
  writes = writes.then(async () => {
    try {
      const db = await database();
      if (!db) return;
      const tx = db.transaction(['plays', 'meta'], 'readwrite');
      // Each request rejects on its own when the transaction fails, so all of them are awaited with it.
      const requests = ops.map((op) => {
        if (op.kind === 'put') return tx.objectStore('plays').put(op.record);
        if (op.kind === 'delete') return tx.objectStore('plays').delete([op.scope, op.cid]);
        if (op.meta) return tx.objectStore('meta').put({ scope: op.scope, ...op.meta });
        return tx.objectStore('meta').delete(op.scope);
      });
      await Promise.all([...requests, tx.done]);
    } catch (cause) {
      console.warn('[plays] could not persist; the memory copy stands', cause);
    }
  });
}

const scopes = new Map<string, ScopeCache>();
const loading = new Map<string, Promise<void>>();

/**
 * Reads a scope from IndexedDB into memory, replacing what memory held. Waits for writes still on their way
 * first, so it reads back everything this page wrote. Called when a page first needs a scope, and again when
 * a tab comes back into view, to pick up what another tab wrote meanwhile.
 */
export function loadScope(scope: string): Promise<void> {
  const pending = loading.get(scope);
  if (pending) return pending;

  const load = (async () => {
    await writes;
    const plays = new Map<string, PlayRecord>();
    let meta: SyncMeta | null = null;
    try {
      const db = await database();
      if (db) {
        for (const record of (await db.getAllFromIndex('plays', 'scope', scope)) as PlayRecord[]) {
          plays.set(record.cid, record);
        }
        const stored = (await db.get('meta', scope)) as (SyncMeta & { scope: string }) | undefined;
        if (stored) {
          const { scope: _scope, ...rest } = stored;
          meta = rest;
        }
      }
    } catch (cause) {
      console.warn('[plays] could not read stored plays', cause);
    }
    /*
      What memory holds is merged in rather than dropped: plays only it has (IndexedDB unavailable, a write
      that failed), and versions another tab overwrote with an older one, a pulled copy say. The merge rule
      picks, as everywhere else; a version of ours that wins is written back.
    */
    const held = scopes.get(scope);
    const rewrite: Op[] = [];
    for (const [cid, ours] of held?.plays ?? []) {
      const stored = plays.get(cid);
      if (!stored || comparePlayVersions(ours, stored) > 0) {
        plays.set(cid, ours);
        rewrite.push({ kind: 'put', record: ours });
      }
    }
    persist(rewrite);
    let loaded = meta ?? held?.meta ?? null;
    // Plays stored before they carried their board's slug were pulled without one: pull everything again.
    if (loaded && loaded.lastFullPullAt !== 0 && [...plays.values()].some((play) => play.boardSlug === undefined)) {
      loaded = { ...loaded, lastFullPullAt: 0 };
      persist([{ kind: 'meta', scope, meta: loaded }]);
    }
    scopes.set(scope, { plays, meta: loaded });
    announce();
  })().finally(() => loading.delete(scope));

  loading.set(scope, load);
  return load;
}

export const isLoaded = (scope: string) => scopes.has(scope);

function cache(scope: string): ScopeCache {
  let held = scopes.get(scope);
  if (!held) {
    held = { plays: new Map(), meta: null };
    scopes.set(scope, held);
  }
  return held;
}

const strip = ({ scope: _scope, changedAt: _changedAt, ...play }: PlayRecord): StoredPlay => play;

/** The sync's view of one scope. Writes inside `transaction` reach IndexedDB together. */
export function scopeStore(scope: string): SyncStore {
  let batch: Op[] | null = null;
  const write = (op: Op) => (batch ? batch.push(op) : persist([op]));

  return {
    readMeta: () => cache(scope).meta,
    writeMeta(meta) {
      cache(scope).meta = meta;
      write({ kind: 'meta', scope, meta });
    },
    get(cid) {
      const record = cache(scope).plays.get(cid);
      return record ? strip(record) : null;
    },
    put(play, dirty) {
      const record: PlayRecord = { ...play, dirty, scope, changedAt: Date.now() };
      cache(scope).plays.set(play.cid, record);
      write({ kind: 'put', record });
    },
    remove(cid) {
      if (cache(scope).plays.delete(cid)) write({ kind: 'delete', scope, cid });
    },
    dirty: (limit) =>
      [...cache(scope).plays.values()]
        .filter((record) => record.dirty)
        .sort((a, b) => a.changedAt - b.changedAt)
        .slice(0, limit)
        .map((record) => {
          const { dirty: _dirty, ...play } = strip(record);
          return play;
        }),
    acknowledged: () =>
      [...cache(scope).plays.values()]
        .filter((record) => record.seq !== null)
        .map(({ cid, dirty }) => ({ cid, dirty })),
    clear() {
      const held = cache(scope);
      for (const cid of held.plays.keys()) write({ kind: 'delete', scope, cid });
      held.plays.clear();
      held.meta = null;
      write({ kind: 'meta', scope, meta: null });
    },
    transaction(fn) {
      batch = [];
      try {
        fn();
      } finally {
        const ops = batch;
        batch = null;
        persist(ops);
      }
    },
  };
}

/** Whether this scope has plays waiting to be pushed. */
export const hasUnsent = (scope: string) => [...(scopes.get(scope)?.plays.values() ?? [])].some((play) => play.dirty);

/** How this scope's copy stands, for the admin readout. `scopes` lists only those this page has loaded. */
export function scopeStatus(scope: string): SyncStatus {
  const plays = [...(scopes.get(scope)?.plays.values() ?? [])];
  const unsent = plays.filter((play) => play.dirty);
  return {
    scope,
    plays: plays.length,
    complete: plays.filter((play) => play.complete).length,
    unsent: unsent.length,
    oldestUnsentAt: unsent.length ? Math.min(...unsent.map((play) => play.changedAt)) : null,
    acknowledged: plays.filter((play) => play.seq !== null).length,
    localOnly: plays.filter((play) => play.seq === null && !play.dirty).length,
    meta: scopes.get(scope)?.meta ?? null,
    scopes: [...scopes].map(([name, held]) => ({ scope: name, plays: held.plays.size })),
  };
}

/** Every scope IndexedDB holds, with its play count: the players this browser has stored, loaded or not. */
export async function storedScopes(): Promise<{ scope: string; plays: number }[]> {
  await writes;
  const db = await database();
  if (!db) return [];
  const counts = new Map<string, number>();
  for (const [scope] of (await db.getAllKeys('plays')) as [string, string][]) {
    counts.set(scope, (counts.get(scope) ?? 0) + 1);
  }
  return [...counts].map(([scope, plays]) => ({ scope, plays })).sort((a, b) => b.plays - a.plays);
}

/** Where plays are being kept: IndexedDB, or memory alone when the browser offers none. */
export async function storageBackend(): Promise<'indexeddb' | 'memory'> {
  return (await database()) ? 'indexeddb' : 'memory';
}

/**
 * Asks the browser to exempt this site's storage from eviction under disk pressure. Chrome grants it by
 * engagement without asking, Firefox asks the player, Safari decides for itself; a refusal changes nothing,
 * since the store is refilled from the server either way.
 *
 * At most once a week. Asking again is worth it, because Chrome may grant later what it refused a player
 * it didn't know yet; asking on every page would put Firefox's prompt back in front of someone who closed
 * it without answering.
 */
export async function requestPersistence(): Promise<void> {
  try {
    const storage = typeof navigator === 'undefined' ? undefined : navigator.storage;
    if (!storage?.persist || (await storage.persisted())) return;

    const lastAsked = Number(localStorage.getItem(LocalStorageKeys.STORAGE_PERSIST_ASKED_AT) ?? 0);
    if (Date.now() - lastAsked < PERSIST_ASK_INTERVAL_MS) return;
    // Recorded before asking, so a prompt that's closed unanswered counts as asked.
    localStorage.setItem(LocalStorageKeys.STORAGE_PERSIST_ASKED_AT, String(Date.now()));

    if (await storage.persist()) announce();
  } catch {
    // Unsupported, or refused outright: storage stays best-effort.
  }
}

const PERSIST_ASK_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

/** Makes this scope's next sync pull everything, as its monthly safety net does. */
export function requestFullPull(scope: string) {
  const store = scopeStore(scope);
  const meta = store.readMeta();
  if (meta) store.writeMeta({ ...meta, lastFullPullAt: 0 });
}

/** Whether this scope has completed a sync, so what's here is the player's whole history. */
export const hasSynced = (scope: string) => scopes.get(scope)?.meta != null;

export const getPlay = (scope: string, cid: string) => scopeStore(scope).get(cid);

const playsOf = (scope: string) => [...(scopes.get(scope)?.plays.values() ?? [])].map(strip);

/** Whether this scope has any play at all, of any board, finished or not. */
export const hasPlays = (scope: string) => (scopes.get(scope)?.plays.size ?? 0) > 0;

/** Every play of one board, replays included. */
export const playsForBoard = (scope: string, boardId: number): StoredPlay[] =>
  playsOf(scope).filter((play) => play.boardId === boardId);

/** Every play of one game's boards dated from `start` to `end`, inclusive. */
export const playsForDates = (scope: string, gameId: number, start: string, end: string): StoredPlay[] =>
  playsOf(scope).filter(
    (play) => play.gameId === gameId && play.boardDate !== null && play.boardDate >= start && play.boardDate <= end
  );

/** Every play of a weekly challenge board. */
export const challengePlays = (scope: string): StoredPlay[] => playsOf(scope).filter((play) => !!play.boardWeek);

/** The boards this scope has completed, one entry per board: what its history says about the player. */
export function completedBoards(scope: string): CompletedBoard[] {
  const boards = new Map<number, CompletedBoard>();
  for (const play of scopes.get(scope)?.plays.values() ?? []) {
    if (play.complete) {
      boards.set(play.boardId, { gameId: play.gameId, boardId: play.boardId, boardSlug: play.boardSlug });
    }
  }
  return [...boards.values()];
}

/** Records a change made in this browser, to be pushed at the next sync unless `send` is false. */
export function saveLocalPlay(scope: string, play: SyncedPlay, send = true) {
  scopeStore(scope).put(play, send);
  announce();
}

/**
 * Carries the plays not yet pushed from one scope to another, and drops the rest of the old one. For
 * signing in: the server moves the old scope's plays to the account, and the full pull that follows brings
 * back everything it already had.
 */
export function moveUnsentPlays(from: string, to: string) {
  const source = scopeStore(from);
  const target = scopeStore(to);
  target.transaction(() => {
    for (const play of source.dirty(Number.MAX_SAFE_INTEGER)) target.put(play, true);
  });
  source.transaction(() => source.clear());
  announce();
}

/*
  Change notification, for the components that draw from here. A counter rather than details: everything
  reading plays re-reads, which is a scan of a few thousand objects at most.
*/
let version = 0;
const listeners = new Set<() => void>();

export function announce() {
  version++;
  listeners.forEach((listener) => listener());
}

export const playsVersion = () => version;

export function onPlaysChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** Test-only: forget every scope and start from an empty database handle. */
export function _resetForTests() {
  scopes.clear();
  loading.clear();
  dbPromise = null;
  writes = Promise.resolve();
}
